/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {ApplicationRef, ComponentFactoryResolver, ComponentRef, Injector} from '@angular/core';
import {Node as ProseMirrorNode} from 'prosemirror-model';
import {NodeSelection, Plugin, PluginKey} from 'prosemirror-state';
import {EditorView, NodeView} from 'prosemirror-view';
import {Subscription} from 'rxjs';
import {ImageViewComponent} from '../components/image-view/image-view.component';

class ImageRezieView implements NodeView {
    /**
     * DOM.
     */
    dom: any;

    /**
     * Editor view.
     */
    view: EditorView;

    /**
     * Get position.
     */
    getPos: () => number;

    /**
     * Application Reference.
     */
    applicationRef: ApplicationRef;

    /**
     * Image component reference.
     */
    imageComponentRef: ComponentRef<ImageViewComponent>;

    /**
     * Resize subscription.
     */
    resizeSubscription: Subscription;

    /**
     * Node.
     */
    node: ProseMirrorNode;

    /**
     * Is updating.
     */
    updating = false;

    /**
     * Default constructor.
     * 
     * @param node Prose Mirror Node.
     * @param view Editor view.
     * @param getPos Position.
     * @param injector Injector.
     */
    constructor(node: ProseMirrorNode, view: EditorView, getPos: () => number, injector: Injector) {
        const dom = document.createElement('image-view');

        const componentFactoryResolver = injector.get(ComponentFactoryResolver);
        this.applicationRef = injector.get(ApplicationRef);

        // Create the component and wire it up with the element
        const factory = componentFactoryResolver.resolveComponentFactory(ImageViewComponent);

        this.imageComponentRef = factory.create(injector, [], dom);
        // Attach to the view so that the change detector knows to run
        this.applicationRef.attachView(this.imageComponentRef.hostView);

        this.setNodeAttributes(node.attrs);
        this.imageComponentRef.instance.view = view;

        this.dom = dom;
        this.view = view;
        this.node = node;
        this.getPos = getPos;

        this.resizeSubscription = this.imageComponentRef.instance.imageResize.subscribe(() => {
            this.handleResize();
        });
    }

    private computeChanges(prevAttrs: Record<string, any>, newAttrs: Record<string, any>): boolean {
        return JSON.stringify(prevAttrs) === JSON.stringify(newAttrs);
    }

    private setNodeAttributes(attrs: Record<string, any>): void {
        this.imageComponentRef.instance.src = attrs['src'];
        this.imageComponentRef.instance.alt = attrs['alt'];
        this.imageComponentRef.instance.title = attrs['title'];
        this.imageComponentRef.instance.outerWidth = attrs['width'];
    }

    /**
     * Handle resize.
     */
    handleResize = (): void => {
        if (this.updating) {
            return;
        }

        const {state, dispatch} = this.view;
        const {tr} = state;

        const transaction = tr.setNodeMarkup(this.getPos(), undefined, {
            ...this.node.attrs,
            width: this.imageComponentRef.instance.outerWidth
        });

        const resolvedPos = transaction.doc.resolve(this.getPos());
        const newSelection = new NodeSelection(resolvedPos);

        transaction.setSelection(newSelection);
        dispatch(transaction);
    }

    /**
     * Update view.
     * 
     * @param node Prose mirror node.
     * @returns Boolean.
     */
    update(node: ProseMirrorNode): boolean {
        if (node.type !== this.node.type) {
            return false;
        }

        this.node = node;

        const changed = this.computeChanges(this.node.attrs, node.attrs);
        if (changed) {
            this.updating = true;
            this.setNodeAttributes(node.attrs);
            this.updating = false;
        }
        return true;
    }

    /**
     * Ignore mutation.
     * 
     * @returns Boolean.
     */
    ignoreMutation(): boolean {
        return true;
    }

    /**
     * Select node.
     */
    selectNode(): void {
        this.imageComponentRef.instance.selected = true;
    }

    /**
     * Deselect node.
     */
    deselectNode(): void {
        this.imageComponentRef.instance.selected = false;
    }

    /**
     * Destroy component.
     */
    destroy(): void {
        this.resizeSubscription.unsubscribe();
        this.applicationRef.detachView(this.imageComponentRef.hostView);
    }
}

const imageResizePlugin = (injector: Injector): Plugin => {
    return new Plugin({
        key: new PluginKey('image-resize'),
        props: {
            nodeViews: {
                image: (node: ProseMirrorNode, view: EditorView, getPos: () => number) => {
                    return new ImageRezieView(node, view, getPos, injector);
                }
            }
        }
    });
};

export default imageResizePlugin;
